import React, { useState, useCallback } from 'react';
import {
  Paper,
  BottomNavigation as MuiBottomNavigation,
  BottomNavigationAction,
  Box,
  Fab,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ExploreIcon from '@mui/icons-material/Explore';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import AddIcon from '@mui/icons-material/Add';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import FitnessCenterOutlinedIcon from '@mui/icons-material/FitnessCenterOutlined';
import PersonIcon from '@mui/icons-material/Person';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/auth.context';
import { usePosts } from '../../context/posts.context';
import { CreateMenu } from '../create-menu/create-menu.component';

/**
 * Mobile bottom navigation bar.
 * 5 tabs: Home, Explore, Create (+), Workouts, Profile.
 * Center "Create" button is an elevated FAB.
 * Tapping Create opens a bottom sheet with post type options.
 */
const BottomNavigationBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { openNewPost, openWorkoutPost, openMealPost, openProgressPost } = usePosts();
  const [createMenuOpen, setCreateMenuOpen] = useState(false);

  const getActiveRoute = () => {
    const path = location.pathname;
    if (path.startsWith('/home')) return 'home';
    if (path.startsWith('/explore') || path.startsWith('/search')) return 'explore';
    if (path.startsWith('/workouts')) return 'workouts';
    if (path.startsWith('/profile')) return 'profile';
    return 'home';
  };

  const handleChange = (event, newValue) => {
    if (newValue === 'profile') {
      const username = profile?.username;
      navigate(username ? `/profile/${username}` : '/profile');
      return;
    }
    navigate(`/${newValue}`);
  };

  const handleCreateOpen = useCallback(() => {
    setCreateMenuOpen(true);
  }, []);

  const handleCreateClose = useCallback(() => {
    setCreateMenuOpen(false);
  }, []);

  const handleQuickPost = useCallback(() => {
    setCreateMenuOpen(false);
    openNewPost();
  }, [openNewPost]);

  const handleLogWorkout = useCallback(() => {
    setCreateMenuOpen(false);
    openWorkoutPost();
  }, [openWorkoutPost]);

  const handleLogMeal = useCallback(() => {
    setCreateMenuOpen(false);
    openMealPost();
  }, [openMealPost]);

  const handleProgressPhoto = useCallback(() => {
    setCreateMenuOpen(false);
    openProgressPost();
  }, [openProgressPost]);

  return (
    <>
      <Paper
        component="nav"
        aria-label="Mobile navigation"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          borderTop: '1px solid',
          borderColor: 'divider',
          pb: 'env(safe-area-inset-bottom, 0px)',
        }}
        elevation={8}
      >
        <Box sx={{ position: 'relative' }}>
          {/* FAB Create button positioned above the nav bar */}
          <Box
            sx={{
              position: 'absolute',
              top: -22,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1,
            }}
          >
            <Fab
              color="primary"
              size="medium"
              onClick={handleCreateOpen}
              aria-label="Create new content"
              sx={{
                boxShadow: 3,
                width: 48,
                height: 48,
              }}
            >
              <AddIcon />
            </Fab>
          </Box>

          <MuiBottomNavigation
            value={getActiveRoute()}
            onChange={handleChange}
            showLabels
            sx={{
              backgroundColor: 'background.paper',
              height: 56,
              '& .MuiBottomNavigationAction-root': {
                minWidth: 0,
                padding: '6px 0',
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: 'primary.main',
                },
                transition: 'color 0.2s',
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.6rem',
                '&.Mui-selected': {
                  fontSize: '0.65rem',
                },
              },
            }}
          >
            <BottomNavigationAction
              value="home"
              label="Home"
              icon={getActiveRoute() === 'home' ? <HomeIcon /> : <HomeOutlinedIcon />}
              aria-label="Home"
            />
            <BottomNavigationAction
              value="explore"
              label="Explore"
              icon={getActiveRoute() === 'explore' ? <ExploreIcon /> : <ExploreOutlinedIcon />}
              aria-label="Explore"
            />
            {/* Spacer for the center FAB */}
            <BottomNavigationAction
              value="create"
              label=""
              icon={<Box sx={{ width: 24, height: 24 }} />}
              sx={{ pointerEvents: 'none', opacity: 0 }}
              aria-hidden="true"
            />
            <BottomNavigationAction
              value="workouts"
              label="Workouts"
              icon={getActiveRoute() === 'workouts' ? <FitnessCenterIcon /> : <FitnessCenterOutlinedIcon />}
              aria-label="Workouts"
            />
            <BottomNavigationAction
              value="profile"
              label="Profile"
              icon={getActiveRoute() === 'profile' ? <PersonIcon /> : <PersonOutlineIcon />}
              aria-label="Profile"
            />
          </MuiBottomNavigation>
        </Box>
      </Paper>

      {/* Create menu bottom sheet */}
      <CreateMenu
        open={createMenuOpen}
        onClose={handleCreateClose}
        onQuickPost={handleQuickPost}
        onLogWorkout={handleLogWorkout}
        onLogMeal={handleLogMeal}
        onProgressPhoto={handleProgressPhoto}
        variant="bottom-sheet"
      />
    </>
  );
};

export default BottomNavigationBar;
